import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Search, Plus, Edit, Trash2, Eye, Copy, Tag, Loader2, AlertCircle, Globe, AlertTriangle, Upload, X } from 'lucide-react';
import { apiService, PromptTemplate, PromptTemplateCategory, CreatePromptTemplateRequest, UpdatePromptTemplateRequest, LanguageConfigResponse } from '../../services/api';
import { toast } from 'sonner';
import { copyToClipboard } from '../../utils/clipboard';
import { LanguageSettingsDialog } from './LanguageSettingsDialog';
import { Settings } from 'lucide-react';

// Supported languages configuration
const SUPPORTED_LANGUAGES = ['de', 'en', 'fr', 'es', 'it'] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
const DEFAULT_LANGUAGE: SupportedLanguage = 'de';

const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Francais',
  es: 'Espanol',
  it: 'Italiano',
};

const categoryDisplayNames: Record<PromptTemplateCategory, string> = {
  [PromptTemplateCategory.GENERAL]: 'General',
  [PromptTemplateCategory.CUSTOMER_SUPPORT]: 'Customer Support',
  [PromptTemplateCategory.SALES]: 'Sales',
  [PromptTemplateCategory.TECHNICAL_SUPPORT]: 'Technical Support',
  [PromptTemplateCategory.EDUCATION]: 'Education',
  [PromptTemplateCategory.HEALTHCARE]: 'Healthcare',
  [PromptTemplateCategory.VOICEBOT]: 'Voice Bot',
  [PromptTemplateCategory.CHATBOT]: 'Chat Bot',
  [PromptTemplateCategory.WARM_TRANSFER]: 'Warm Transfer',
};

const categories = Object.values(PromptTemplateCategory);

interface FormData {
  nameI18n: Record<string, string>;
  descriptionI18n: Record<string, string>;
  category: PromptTemplateCategory;
  promptContentI18n: Record<string, string>;
  isActive: boolean;
}

export function PromptTemplatesPage() {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | { message?: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage] = useState(1);
  const [_totalPages, setTotalPages] = useState(0);
  const [_total, setTotal] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState<PromptTemplate | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null);
  const [_isSaving, setIsSaving] = useState(false);
  const [activeLanguageTab, setActiveLanguageTab] = useState<SupportedLanguage>(DEFAULT_LANGUAGE);
  const [_languageConfig, setLanguageConfig] = useState<LanguageConfigResponse | null>(null);
  const [isLanguageSettingsOpen, setIsLanguageSettingsOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    nameI18n: { de: '' },
    descriptionI18n: { de: '' },
    category: PromptTemplateCategory.GENERAL,
    promptContentI18n: { de: '' },
    isActive: true
  });
  const [isIconUploading, setIsIconUploading] = useState(false);
  const [isIconDeleting, setIsIconDeleting] = useState(false);
  const [iconError, setIconError] = useState<string | null>(null);

  // Load language configuration on mount
  useEffect(() => {
    loadLanguageConfig();
  }, []);

  // Load templates on component mount and when filters change
  useEffect(() => {
    loadTemplates();
  }, [currentPage, selectedCategory, searchTerm]);

  const loadLanguageConfig = async () => {
    try {
      const config = await apiService.getLanguageConfig();
      setLanguageConfig(config);
    } catch (err) {
      console.error('Failed to load language config:', err);
      // Fallback to default if server unavailable
    }
  };

  // Callback when language config changes
  const handleLanguageConfigChange = () => {
    loadLanguageConfig();
    loadTemplates(); // Reload templates to get updated missingLanguages
  };

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getPromptTemplates({
        page: currentPage,
        limit: 20,
        category: selectedCategory && selectedCategory !== 'all' ? selectedCategory as PromptTemplateCategory : undefined,
        search: searchTerm || undefined,
      });
      setTemplates(response.templates);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      setError(err.message || 'Failed to load templates');
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = (template: PromptTemplate) => {
    setViewingTemplate(template);
    // Set the active tab to the first available language or default
    const availableLangs = template.availableLanguages || [];
    if (availableLangs.length > 0 && SUPPORTED_LANGUAGES.includes(availableLangs[0] as SupportedLanguage)) {
      setActiveLanguageTab(availableLangs[0] as SupportedLanguage);
    } else {
      setActiveLanguageTab(DEFAULT_LANGUAGE);
    }
    setIsDialogOpen(true);
  };

  const handleEdit = (template: PromptTemplate) => {
    setEditingTemplate(template);
    // Initialize i18n content from existing template
    const nameI18n: Record<string, string> = template.nameI18n || {};
    // If no i18n content but has legacy name, use it as German
    if (Object.keys(nameI18n).length === 0 && template.name) {
      nameI18n.de = template.name;
    }
    const descriptionI18n: Record<string, string> = template.descriptionI18n || {};
    // If no i18n content but has legacy description, use it as German
    if (Object.keys(descriptionI18n).length === 0 && template.description) {
      descriptionI18n.de = template.description;
    }
    const promptContentI18n: Record<string, string> = template.promptContentI18n || {};
    // If no i18n content but has legacy content, use it as German
    if (Object.keys(promptContentI18n).length === 0 && template.promptContent) {
      promptContentI18n.de = template.promptContent;
    }
    setFormData({
      nameI18n,
      descriptionI18n,
      category: template.category,
      promptContentI18n,
      isActive: template.isActive
    });
    setActiveLanguageTab(DEFAULT_LANGUAGE);
    setViewingTemplate(null);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Validate that at least one language has name
      const hasName = Object.values(formData.nameI18n).some(name => name?.trim());
      if (!hasName) {
        toast.error('Bitte geben Sie den Namen in mindestens einer Sprache ein');
        setIsSaving(false);
        return;
      }

      // Validate that at least one language has prompt content
      const hasContent = Object.values(formData.promptContentI18n).some(content => content?.trim());
      if (!hasContent) {
        toast.error('Bitte geben Sie den Prompt-Text in mindestens einer Sprache ein');
        setIsSaving(false);
        return;
      }

      // Clean empty language entries for all i18n fields
      const cleanedNameI18n: Record<string, string> = {};
      for (const [lang, name] of Object.entries(formData.nameI18n)) {
        if (name?.trim()) {
          cleanedNameI18n[lang] = name.trim();
        }
      }

      const cleanedDescriptionI18n: Record<string, string> = {};
      for (const [lang, desc] of Object.entries(formData.descriptionI18n)) {
        if (desc?.trim()) {
          cleanedDescriptionI18n[lang] = desc.trim();
        }
      }

      const cleanedPromptContentI18n: Record<string, string> = {};
      for (const [lang, content] of Object.entries(formData.promptContentI18n)) {
        if (content?.trim()) {
          cleanedPromptContentI18n[lang] = content.trim();
        }
      }

      if (editingTemplate) {
        // Update existing template
        const updateData: UpdatePromptTemplateRequest = {
          nameI18n: cleanedNameI18n,
          descriptionI18n: Object.keys(cleanedDescriptionI18n).length > 0 ? cleanedDescriptionI18n : undefined,
          category: formData.category,
          promptContentI18n: cleanedPromptContentI18n,
          isActive: formData.isActive,
        };
        await apiService.updatePromptTemplate(editingTemplate.id, updateData);
        toast.success('Template updated successfully');
      } else {
        // Create new template
        const createData: CreatePromptTemplateRequest = {
          nameI18n: cleanedNameI18n,
          descriptionI18n: Object.keys(cleanedDescriptionI18n).length > 0 ? cleanedDescriptionI18n : undefined,
          category: formData.category,
          promptContentI18n: cleanedPromptContentI18n,
          isActive: formData.isActive,
        };
        await apiService.createPromptTemplate(createData);
        toast.success('Template created successfully');
      }

      closeDialog();
      await loadTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to save template');
      toast.error(err.message || 'Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this template?')) {
      try {
        await apiService.deletePromptTemplate(id);
        toast.success('Template deleted successfully');
        await loadTemplates();
      } catch (err: any) {
        toast.error(err.message || 'Failed to delete template');
      }
    }
  };

  const toggleActive = async (template: PromptTemplate) => {
    try {
      const updateData: UpdatePromptTemplateRequest = {
        isActive: !template.isActive,
      };
      await apiService.updatePromptTemplate(template.id, updateData);
      toast.success(`Template ${template.isActive ? 'deactivated' : 'activated'} successfully`);
      await loadTemplates();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update template');
    }
  };

  const handleIconUpload = async (file: File) => {
    setIconError(null);

    if (!file.name.toLowerCase().endsWith('.svg') && file.type !== 'image/svg+xml') {
      setIconError('Nur SVG-Dateien sind erlaubt.');
      return;
    }

    if (file.size > 500 * 1024) {
      setIconError('Die Datei darf maximal 500 KB gross sein.');
      return;
    }

    if (!editingTemplate) return;

    try {
      setIsIconUploading(true);
      const updated = await apiService.uploadPromptTemplateIcon(editingTemplate.id, file);
      setEditingTemplate(updated);
      setTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
      toast.success('Icon erfolgreich hochgeladen');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : (err as { message?: string })?.message ?? 'Upload fehlgeschlagen';
      setIconError(message);
      toast.error(message);
    } finally {
      setIsIconUploading(false);
    }
  };

  const handleIconDelete = async () => {
    if (!editingTemplate) return;

    try {
      setIsIconDeleting(true);
      setIconError(null);
      const updated = await apiService.deletePromptTemplateIcon(editingTemplate.id);
      setEditingTemplate(updated);
      setTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
      toast.success('Icon erfolgreich entfernt');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : (err as { message?: string })?.message ?? 'Fehler beim Entfernen des Icons';
      setIconError(message);
      toast.error(message);
    } finally {
      setIsIconDeleting(false);
    }
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingTemplate(null);
    setViewingTemplate(null);
    setIsSaving(false);
    setIconError(null);
    setActiveLanguageTab(DEFAULT_LANGUAGE);
    setFormData({
      nameI18n: { de: '' },
      descriptionI18n: { de: '' },
      category: PromptTemplateCategory.GENERAL,
      promptContentI18n: { de: '' },
      isActive: true
    });
  };

  const updateNameContent = (lang: string, content: string) => {
    setFormData(prev => ({
      ...prev,
      nameI18n: {
        ...prev.nameI18n,
        [lang]: content
      }
    }));
  };

  const updateDescriptionContent = (lang: string, content: string) => {
    setFormData(prev => ({
      ...prev,
      descriptionI18n: {
        ...prev.descriptionI18n,
        [lang]: content
      }
    }));
  };

  const updatePromptContent = (lang: string, content: string) => {
    setFormData(prev => ({
      ...prev,
      promptContentI18n: {
        ...prev.promptContentI18n,
        [lang]: content
      }
    }));
  };

  const getNameForLanguage = (template: PromptTemplate, lang: string): string => {
    if (template.nameI18n && template.nameI18n[lang]) {
      return template.nameI18n[lang];
    }
    // Fallback to legacy name for German
    if (lang === 'de' && template.name) {
      return template.name;
    }
    return '';
  };

  const getDescriptionForLanguage = (template: PromptTemplate, lang: string): string => {
    if (template.descriptionI18n && template.descriptionI18n[lang]) {
      return template.descriptionI18n[lang];
    }
    // Fallback to legacy description for German
    if (lang === 'de' && template.description) {
      return template.description;
    }
    return '';
  };

  const getContentForLanguage = (template: PromptTemplate, lang: string): string => {
    if (template.promptContentI18n && template.promptContentI18n[lang]) {
      return template.promptContentI18n[lang];
    }
    // Fallback to legacy content for German
    if (lang === 'de' && template.promptContent) {
      return template.promptContent;
    }
    return '';
  };

  const hasContentForLanguage = (template: PromptTemplate, lang: string): boolean => {
    const hasName = !!(template.nameI18n?.[lang] || (lang === 'de' && template.name));
    const hasPrompt = !!(template.promptContentI18n?.[lang] || (lang === 'de' && template.promptContent));
    return hasName || hasPrompt;
  };

  const getCategoryBadgeColor = (category: PromptTemplateCategory) => {
    const colors: Record<PromptTemplateCategory, string> = {
      [PromptTemplateCategory.GENERAL]: 'bg-gray-100 text-gray-800',
      [PromptTemplateCategory.CUSTOMER_SUPPORT]: 'bg-blue-100 text-blue-800',
      [PromptTemplateCategory.SALES]: 'bg-green-100 text-green-800',
      [PromptTemplateCategory.TECHNICAL_SUPPORT]: 'bg-purple-100 text-purple-800',
      [PromptTemplateCategory.EDUCATION]: 'bg-indigo-100 text-indigo-800',
      [PromptTemplateCategory.HEALTHCARE]: 'bg-red-100 text-red-800',
      [PromptTemplateCategory.VOICEBOT]: 'bg-yellow-100 text-yellow-800',
      [PromptTemplateCategory.CHATBOT]: 'bg-pink-100 text-pink-800',
      [PromptTemplateCategory.WARM_TRANSFER]: 'bg-violet-100 text-violet-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getAvailableLanguagesDisplay = (template: PromptTemplate): string[] => {
    return template.availableLanguages || [];
  };

  // Check if template has missing translations
  const hasMissingTranslations = (template: PromptTemplate): boolean => {
    return (template.missingLanguages?.length ?? 0) > 0;
  };

  // Get missing languages for a template
  const getMissingLanguages = (template: PromptTemplate): string[] => {
    return template.missingLanguages || [];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Prompt Templates</h1>
          <p className="text-gray-600">Verwaltung wiederverwendbarer Prompt-Vorlagen</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setIsLanguageSettingsOpen(true)}
            disabled={isLoading}
          >
            <Settings className="w-4 h-4 mr-2" />
            Sprachen
          </Button>
          <Button onClick={() => { setViewingTemplate(null); setEditingTemplate(null); setIsDialogOpen(true); }} disabled={isLoading}>
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            disabled={isLoading}
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory} disabled={isLoading}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category} value={category}>
                {categoryDisplayNames[category]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading templates...</span>
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{typeof error === 'string' ? error : (error?.message || 'An error occurred')}</p>
            <Button onClick={loadTemplates}>Try Again</Button>
          </CardContent>
        </Card>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500 mb-4">No templates found.</p>
            <Button onClick={() => { setViewingTemplate(null); setEditingTemplate(null); setIsDialogOpen(true); }}>
              Create First Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
          <Card
            key={template.id}
            className={`hover:shadow-md transition-shadow ${
              hasMissingTranslations(template) ? 'border-2 border-yellow-400' : ''
            }`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg">{template.name}</CardTitle>
                  <CardDescription className="mt-1">{template.description}</CardDescription>
                </div>
                <Switch
                  checked={template.isActive}
                  onCheckedChange={() => toggleActive(template)}
                  disabled={isLoading}
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <Badge className={getCategoryBadgeColor(template.category)}>
                  <Tag className="w-3 h-3 mr-1" />
                  {categoryDisplayNames[template.category]}
                </Badge>
                <div className="flex items-center gap-2">
                  {/* Missing translations warning badge */}
                  {hasMissingTranslations(template) && (
                    <div
                      className="flex items-center gap-1 bg-yellow-50 text-yellow-600 px-2 py-1 rounded"
                      title={`Fehlende Uebersetzungen: ${getMissingLanguages(template).map(l => LANGUAGE_LABELS[l as SupportedLanguage] || l).join(', ')}`}
                    >
                      <AlertTriangle className="w-3 h-3" />
                      <span className="text-xs font-medium">{getMissingLanguages(template).length}</span>
                    </div>
                  )}
                  {getAvailableLanguagesDisplay(template).length > 0 && (
                    <div className="flex items-center gap-1">
                      <Globe className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        {getAvailableLanguagesDisplay(template).map(l => l.toUpperCase()).join(', ')}
                      </span>
                    </div>
                  )}
                  {template.isSystemTemplate && (
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">System</span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 max-h-24 overflow-y-auto">
                  {template.promptContent.substring(0, 150)}
                  {template.promptContent.length > 150 && '...'}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    Created: {new Date(template.createdAt).toLocaleDateString()}
                  </span>
                  <div className="flex items-center space-x-1">
                    <Button variant="ghost" size="sm" onClick={() => handleView(template)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(template)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        void copyToClipboard(template.promptContent, 'Prompt');
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(template.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {viewingTemplate ? 'Template Details' :
               editingTemplate ? 'Template bearbeiten' : 'Neue Template erstellen'}
            </DialogTitle>
            <DialogDescription>
              {viewingTemplate ? 'Detaillierte Ansicht der Prompt Template' :
               'Erstellen oder bearbeiten Sie eine wiederverwendbare Prompt Template'}
            </DialogDescription>
          </DialogHeader>

          {viewingTemplate ? (
            <div className="space-y-6">
              {/* Template metadata at top */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2 text-sm">
                  <div><strong>Kategorie:</strong>
                    <Badge className={`ml-2 ${getCategoryBadgeColor(viewingTemplate.category)}`}>
                      {categoryDisplayNames[viewingTemplate.category]}
                    </Badge>
                  </div>
                  <div><strong>Status:</strong> {viewingTemplate.isActive ? 'Aktiv' : 'Inaktiv'}</div>
                </div>
                <div className="space-y-2 text-sm">
                  <div><strong>Erstellt:</strong> {new Date(viewingTemplate.createdAt).toLocaleDateString('de-DE')}</div>
                  <div><strong>System Template:</strong> {viewingTemplate.isSystemTemplate ? 'Ja' : 'Nein'}</div>
                  <div><strong>Verfuegbare Sprachen:</strong> {getAvailableLanguagesDisplay(viewingTemplate).map(l => LANGUAGE_LABELS[l as SupportedLanguage] || l).join(', ') || 'Keine'}</div>
                </div>
              </div>

              {/* Name and Description above tabs */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Name</h4>
                  <p className="text-base font-medium text-gray-900">{viewingTemplate.name}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Beschreibung</h4>
                  <p className="text-sm text-gray-600">{viewingTemplate.description || <span className="text-gray-400 italic">Keine Beschreibung</span>}</p>
                </div>
              </div>

              {/* Language tabs with i18n name, description, and prompt content */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">Uebersetzungen</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      void copyToClipboard(getContentForLanguage(viewingTemplate, activeLanguageTab), 'Prompt');
                    }}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Prompt Kopieren
                  </Button>
                </div>
                <Tabs value={activeLanguageTab} onValueChange={(v) => setActiveLanguageTab(v as SupportedLanguage)}>
                  <TabsList className="mb-2">
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <TabsTrigger
                        key={lang}
                        value={lang}
                        className={!hasContentForLanguage(viewingTemplate, lang) ? 'opacity-50' : ''}
                      >
                        {LANGUAGE_LABELS[lang]}
                        {hasContentForLanguage(viewingTemplate, lang) && (
                          <span className="ml-1 w-2 h-2 bg-green-500 rounded-full inline-block" />
                        )}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <TabsContent key={lang} value={lang} className="space-y-4">
                      {/* i18n Name - only show for non-German languages */}
                      {lang !== DEFAULT_LANGUAGE && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-1">Name ({LANGUAGE_LABELS[lang]})</h5>
                          <p className="text-sm text-gray-800 bg-gray-50 rounded-lg p-2">
                            {getNameForLanguage(viewingTemplate, lang) || <span className="text-gray-400 italic">Nicht vorhanden</span>}
                          </p>
                        </div>
                      )}

                      {/* i18n Description - only show for non-German languages */}
                      {lang !== DEFAULT_LANGUAGE && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-1">Beschreibung ({LANGUAGE_LABELS[lang]})</h5>
                          <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-2">
                            {getDescriptionForLanguage(viewingTemplate, lang) || <span className="text-gray-400 italic">Nicht vorhanden</span>}
                          </p>
                        </div>
                      )}

                      {/* i18n Prompt content */}
                      <div>
                        <h5 className="text-sm font-medium text-gray-700 mb-1">Prompt ({LANGUAGE_LABELS[lang]})</h5>
                        <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                          {getContentForLanguage(viewingTemplate, lang) ? (
                            <pre className="whitespace-pre-wrap text-sm text-gray-800">
                              {getContentForLanguage(viewingTemplate, lang)}
                            </pre>
                          ) : (
                            <p className="text-gray-400 italic">Kein Inhalt in dieser Sprache vorhanden</p>
                          )}
                        </div>
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={closeDialog}>
                  Schliessen
                </Button>
                <Button onClick={() => handleEdit(viewingTemplate)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Bearbeiten
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Icon Upload Section — only shown when editing an existing template */}
              {editingTemplate && (
                <div className="space-y-2">
                  <Label>Icon</Label>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 flex items-center justify-center border rounded-lg bg-gray-50 overflow-hidden shrink-0">
                      {editingTemplate.iconUrl ? (
                        <img
                          src={editingTemplate.iconUrl}
                          alt="Template Icon"
                          className="w-12 h-12 object-contain"
                        />
                      ) : (
                        <span className="text-xs text-gray-400 text-center leading-tight px-1">Kein Icon</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor="icon-file-input"
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 cursor-pointer transition-colors ${isIconUploading ? 'opacity-50 pointer-events-none' : ''}`}
                        >
                          {isIconUploading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4" />
                          )}
                          SVG hochladen
                        </label>
                        <input
                          id="icon-file-input"
                          type="file"
                          accept=".svg"
                          className="sr-only"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleIconUpload(file);
                              e.target.value = '';
                            }
                          }}
                          disabled={isIconUploading || isIconDeleting}
                        />
                        {editingTemplate.iconUrl && (
                          <button
                            type="button"
                            onClick={handleIconDelete}
                            disabled={isIconUploading || isIconDeleting}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-red-200 text-red-600 bg-white hover:bg-red-50 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                          >
                            {isIconDeleting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                            Icon entfernen
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">Nur SVG-Dateien, max. 500 KB</p>
                    </div>
                  </div>
                  {iconError && (
                    <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{iconError}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Category and Active Status at top */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Kategorie</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value as PromptTemplateCategory})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Kategorie waehlen" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {categoryDisplayNames[category]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2 pt-8">
                  <Switch
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) => setFormData({...formData, isActive: checked})}
                  />
                  <Label htmlFor="isActive">Template aktiv</Label>
                </div>
              </div>

              {/* Name and Description above tabs (editing German/default) */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.nameI18n['de'] || ''}
                    onChange={(e) => updateNameContent('de', e.target.value)}
                    placeholder="Template Name..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Beschreibung</Label>
                  <Textarea
                    id="description"
                    value={formData.descriptionI18n['de'] || ''}
                    onChange={(e) => updateDescriptionContent('de', e.target.value)}
                    placeholder="Beschreiben Sie den Zweck und die Verwendung dieser Template..."
                    className="min-h-20"
                  />
                </div>
              </div>

              {/* Multilingual content tabs */}
              <div className="space-y-2">
                <Label>Uebersetzungen</Label>
                <Tabs value={activeLanguageTab} onValueChange={(v) => setActiveLanguageTab(v as SupportedLanguage)}>
                  <TabsList className="mb-2">
                    {SUPPORTED_LANGUAGES.map((lang) => {
                      const hasContent = formData.nameI18n[lang]?.trim() || formData.promptContentI18n[lang]?.trim();
                      return (
                        <TabsTrigger key={lang} value={lang}>
                          {LANGUAGE_LABELS[lang]}
                          {hasContent && (
                            <span className="ml-1 w-2 h-2 bg-green-500 rounded-full inline-block" />
                          )}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <TabsContent key={lang} value={lang} className="space-y-4">
                      {/* i18n Name input - only show for non-German languages */}
                      {lang !== DEFAULT_LANGUAGE && (
                        <div className="space-y-2">
                          <Label htmlFor={`name-${lang}`}>Name ({LANGUAGE_LABELS[lang]})</Label>
                          <Input
                            id={`name-${lang}`}
                            value={formData.nameI18n[lang] || ''}
                            onChange={(e) => updateNameContent(lang, e.target.value)}
                            placeholder={`Template Name auf ${LANGUAGE_LABELS[lang]}...`}
                          />
                        </div>
                      )}

                      {/* i18n Description textarea - only show for non-German languages */}
                      {lang !== DEFAULT_LANGUAGE && (
                        <div className="space-y-2">
                          <Label htmlFor={`description-${lang}`}>Beschreibung ({LANGUAGE_LABELS[lang]})</Label>
                          <Textarea
                            id={`description-${lang}`}
                            value={formData.descriptionI18n[lang] || ''}
                            onChange={(e) => updateDescriptionContent(lang, e.target.value)}
                            placeholder={`Beschreibung auf ${LANGUAGE_LABELS[lang]}...`}
                            className="min-h-20"
                          />
                        </div>
                      )}

                      {/* i18n Prompt content textarea */}
                      <div className="space-y-2">
                        <Label htmlFor={`prompt-${lang}`}>Prompt ({LANGUAGE_LABELS[lang]})</Label>
                        <Textarea
                          id={`prompt-${lang}`}
                          value={formData.promptContentI18n[lang] || ''}
                          onChange={(e) => updatePromptContent(lang, e.target.value)}
                          placeholder={`Geben Sie hier den Prompt-Text auf ${LANGUAGE_LABELS[lang]} ein...`}
                          className="min-h-48"
                        />
                      </div>

                      <p className="text-xs text-gray-500">
                        {lang === DEFAULT_LANGUAGE
                          ? 'Deutsch ist die Standard-Sprache und wird als Fallback verwendet.'
                          : 'Optional - wenn leer, wird die deutsche Version verwendet.'}
                      </p>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={closeDialog}>
                  Abbrechen
                </Button>
                <Button onClick={handleSave}>
                  {editingTemplate ? 'Speichern' : 'Erstellen'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Language Settings Dialog */}
      <LanguageSettingsDialog
        open={isLanguageSettingsOpen}
        onOpenChange={setIsLanguageSettingsOpen}
        onConfigChange={handleLanguageConfigChange}
      />
    </div>
  );
}
